"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.dispatch = dispatch;
exports.highestVersion = highestVersion;
exports.listen = listen;
exports.stamp = stamp;
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* global globalThis */
/**
 * The default namespace for our communication protocol. Something unique but
 * consistent enough that every instance can reference without clashing with
 * existing custom events.
 *
 * @type {String}
 * @private
 */
const tuah = '__The_Unknown_Algorythm_Hash__';

/**
 * We need add our execution timing to the version to ensure that we create a
 * unique version for each instance. This prevents duplicate versions and allows
 * us to resolve to a single highest version to correctly determine the leader.
 *
 * Using the performance.now() as timestamp should be accurate enough as there
 * should only be a single instance of our consensus protocol included per
 * bundle. There is enough precision in the timestamp to ensure that we get a
 * different timestamp for the next JavaScript file/bundle that is executed.
 *
 * @param {String} version Version number, in the format of `major.minor.patch`.
 * @param {String} id Unique identifier for the instance.
 * @returns {String} Version number with a timestamp.
 * @private
 */
function stamp(version, id) {
  return `${version}.${id}`;
}

/**
 * Dispatches a message over the consensus protocol.
 *
 * @param {Object} options Dispatch options.
 * @param {String} options.action The action that the protocol needs to process.
 * @param {String} options.version Component version.
 * @param {String} options.namespace Protocol namespace.
 * @param {String} options.id Unique identifier for the instance.
 * @param {Object} payload Payload to send.
 * @public
 */
function dispatch(_ref) {
  let {
    action = 'message',
    namespace = tuah,
    id,
    version
  } = _ref;
  let payload = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  globalThis.dispatchEvent(new CustomEvent(namespace, {
    detail: _objectSpread(_objectSpread({}, payload), {}, {
      [namespace + ':version']: stamp(version, id),
      [namespace + ':action']: action
    })
  }));
}

/**
 * Determine if our version is the highest version.
 *
 * @param {String} ours Our version number, in the format of `major.minor.patch.${now}`.
 * @param {String} their Their version number, in the format of `major.minor.patch.${now}`.
 * @returns {Boolean} Indication that our version is the highest.
 * @private
 */
function highestVersion(ours, their) {
  const ourTokens = ours.split('.');
  const theirTokens = their.split('.');
  for (let i = 0; i < ourTokens.length; i++) {
    const ourValue = +ourTokens[i];
    const theirValue = +theirTokens[i];
    if (ourValue > theirValue) return true;
    if (ourValue < theirValue) return false;
  }

  //
  // If we've reached this point, it means that the versions are identical.
  // We will consider our version to be the highest. This should really never
  // happen as we add a timestamp to the version to ensure uniqueness. Cross
  // your fingers and hope for the best.
  //
  return true;
}

/**
 * Mount the consensus protocol and start listening, responding to messages if
 * we are elected as the leading instance.
 *
 * @param {Object} options Mounting specific options.
 * @param {String} options.version Version number of the component.
 * @param {String} options.id Unique identifier for the instance.
 * @param {String} options.namespace Namespace for the protocol.
 * @param {Function} onmessage Message handler, using an error first callback style.
 * @returns {Function} Unmount function.
 * @public
 */
function listen() {
  let {
    version,
    id,
    namespace = tuah
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let onmessage = arguments.length > 1 ? arguments[1] : undefined;
  let leader = null;

  /**
   * Our consensus "algorithm" for multiple execution contexts.
   *
   * @param {Object} event Event object.
   * @private
   */
  function consensus(_ref2) {
    let {
      detail
    } = _ref2;
    const {
        [namespace + ':version']: theirVersion,
        // The version of their component.
        [namespace + ':action']: action // Action to process.
        // Message payload.
      } = detail,
      payload = (0, _objectWithoutProperties2.default)(detail, [namespace + ':version', namespace + ':action'].map(_toPropertyKey));
    switch (action) {
      //
      // Action(message):
      //
      // The message is only to be processed by the leader. All other instances
      // should ignore this action.
      //
      case 'message':
        if (leader) onmessage(null, payload, {
          version: theirVersion,
          namespace,
          action
        });
        break;

      //
      // Action(candidate):
      //
      // The instance is declaring itself as a candidate for leadership. In
      // order to become the leader, the instance must have the highest of all
      // available instances. We will promote our instance to leader when we've
      // determined that we have the highest version of all.
      //
      case 'candidate':
        if (leader !== false) {
          leader = highestVersion(stamp(version, id), theirVersion);
        }
        break;

      //
      // Action(promote):
      //
      // An instance has mounted or unmouted, we will need to start a promotion
      // process to determine if we are still the leader. It could be that a
      // newer version has been mounted or unmounted forcing us to find the new
      // leader. Only instances that are currently mounted are allowed to
      // announce themselves as a candidate for leadership.
      //
      case 'promote':
        leader = null;
        dispatch({
          action: 'candidate',
          version,
          namespace,
          id
        });
        break;

      //
      // Action(unknown):
      //
      // Assume malicious intent, we should not be receiving messages that we
      // don't know how to process.
      //
      default:
        break;
    }
  }
  globalThis.addEventListener(namespace, consensus);
  dispatch({
    action: 'promote',
    version,
    namespace,
    id
  });

  /**
   * Unmount the protocol listener.
   *
   * @public
   */
  return function unmount() {
    globalThis.removeEventListener(namespace, consensus);
    dispatch({
      action: 'promote',
      version,
      namespace,
      id
    });
  };
}